/**
 * Read the exported model.
 *
 * `web/tools/export_model.py` writes the files. This file reads them back. The two must change
 * together, and `FORMAT_VERSION` is what catches a pair that did not.
 *
 * ## The binary block
 *
 * | Part | Type | Count |
 * | --- | --- | --- |
 * | magic `AIDT` | bytes | 4 |
 * | format version | uint32 | 1 |
 * | feature count | uint32 | 1 |
 * | idf level count | uint32 | 1 |
 * | n-gram length | uint32 | 1 |
 * | the idf levels | float64 | level count |
 * | one level index per feature | uint16 | feature count |
 * | one coefficient per feature | float32 | feature count |
 *
 * Every number is little-endian.
 *
 * ## Why the parser copies rather than views
 *
 * A typed array view needs its byte offset to be a multiple of its element size. The three blocks
 * happen to align today, and a future model with a different level count would not. The parser
 * copies instead, so a change of size cannot turn into a runtime error.
 */

import type { Detector, DetectorMeta, Manifest } from './types';

const MAGIC = 'AIDT';
const HEADER_BYTES = 20;

/**
 * The layout that this build reads.
 *
 * **Version 5 names a list of detectors again.** Version 4 named one rather than a
 * list, and no file carries a `confidence` field. A stale export therefore fails here rather than
 * half-working.
 */
export const FORMAT_VERSION = 5;

export class ModelError extends Error {}

/** Return a little-endian slice as a typed array, copying so that alignment cannot fail. */
function read<T>(
  buffer: ArrayBuffer,
  offset: number,
  count: number,
  Type: { new (length: number): T; BYTES_PER_ELEMENT: number },
): T {
  const bytes = count * Type.BYTES_PER_ELEMENT;
  if (offset + bytes > buffer.byteLength) {
    throw new ModelError(`the weights file is short: ${offset + bytes} needed, ${buffer.byteLength} held`);
  }
  const target = new Type(count) as T & ArrayBufferView;
  new Uint8Array(target.buffer).set(new Uint8Array(buffer, offset, bytes));
  return target;
}

/**
 * Build a detector from the three decompressed parts.
 *
 * The function is pure, so the parity check can feed it bytes from disk and the browser can feed
 * it bytes from the network. Neither path gets its own copy of the parser.
 */
export function parseDetector(meta: DetectorMeta, vocab: string, weights: ArrayBuffer): Detector {
  if (meta.format_version !== FORMAT_VERSION) {
    throw new ModelError(
      `detector.json is format ${meta.format_version}, and this build reads ${FORMAT_VERSION}. ` +
        'Run `uv run web/tools/export_model.py` again',
    );
  }

  const head = new DataView(weights);
  const magic = String.fromCharCode(...new Uint8Array(weights, 0, 4));
  if (magic !== MAGIC) {
    throw new ModelError(`the weights file does not start with ${MAGIC}`);
  }

  const version = head.getUint32(4, true);
  const nFeatures = head.getUint32(8, true);
  const nLevels = head.getUint32(12, true);
  const ngram = head.getUint32(16, true);

  if (version !== FORMAT_VERSION) {
    throw new ModelError(`the weights file is format ${version}, and this build reads ${FORMAT_VERSION}`);
  }
  if (nFeatures !== meta.n_features) {
    throw new ModelError(`detector.json holds ${meta.n_features} features and the weights hold ${nFeatures}`);
  }
  if (ngram !== meta.ngram) {
    throw new ModelError(
      `the weights say char_${ngram} and detector.json says char_${meta.ngram}`,
    );
  }
  if (vocab.length !== nFeatures * ngram) {
    throw new ModelError(
      `the vocabulary holds ${vocab.length} code units, and ${nFeatures} keys of ` +
        `${ngram} code points need ${nFeatures * ngram}`,
    );
  }

  let offset = HEADER_BYTES;
  const idfLevels = read(weights, offset, nLevels, Float64Array);
  offset += nLevels * Float64Array.BYTES_PER_ELEMENT;
  const idfIndex = read(weights, offset, nFeatures, Uint16Array);
  offset += nFeatures * Uint16Array.BYTES_PER_ELEMENT;
  const coef = read(weights, offset, nFeatures, Float32Array);

  return { meta, vocab, idfLevels, idfIndex, coef };
}

/** The first two bytes of a gzip member. See RFC 1952. */
const GZIP_MAGIC = [0x1f, 0x8b];

/**
 * Return the bytes of a body that may or may not still be compressed.
 *
 * **A host decides this, and not the code.** A file named `.gz` is served two ways in the wild:
 *
 * 1. As bytes, with no content encoding. The browser hands over the gzip member, and this function
 *    decompresses it.
 * 2. With `Content-Encoding: gzip`. The browser decompresses it on arrival, and the body is
 *    already plain. The Vite dev server does this, and so do several static hosts.
 *
 * Decompressing case 2 again throws, and the page then reports that the model did not load. The
 * function therefore reads the magic number rather than trusting the file name. Both cases send
 * the same compressed bytes over the wire, so neither one costs transfer.
 */
export async function readMaybeGzip(data: ArrayBuffer | Blob): Promise<ArrayBuffer> {
  const blob = data instanceof Blob ? data : new Blob([data]);
  const head = new Uint8Array(await blob.slice(0, 2).arrayBuffer());

  if (head[0] !== GZIP_MAGIC[0] || head[1] !== GZIP_MAGIC[1]) {
    return blob.arrayBuffer();
  }

  if (typeof DecompressionStream === 'undefined') {
    throw new ModelError('this browser holds no DecompressionStream, so it cannot read the model');
  }
  const stream = blob.stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).arrayBuffer();
}

/**
 * Fetch and build one detector from its own folder.
 *
 * The two large files are gzipped on disk and read back with `readMaybeGzip`, so the transfer
 * stays small whatever the host does about content encoding.
 */
export async function fetchOne(base: string, slug: string): Promise<Detector> {
  const folder = `${base}/${slug}`;

  const get = async (name: string): Promise<Response> => {
    const response = await fetch(`${folder}/${name}`);
    if (!response.ok) {
      throw new ModelError(`${folder}/${name} did not load: ${response.status}`);
    }
    return response;
  };

  const meta = (await (await get('detector.json')).json()) as DetectorMeta;
  const vocab = new TextDecoder().decode(
    await readMaybeGzip(await (await get('vocab.txt.gz')).blob()),
  );
  const weights = await readMaybeGzip(await (await get('weights.bin.gz')).blob());
  return parseDetector(meta, vocab, weights);
}

/**
 * Fetch every detector that the manifest names, the verdict first.
 *
 * **The manifest names a list.** The build shipped two detectors until 2026-08-18, one until item
 * 36, and three after it. The page must check the format version before it fetches about 1.3 MB
 * of model, which is why the version sits in the manifest as well as in each file.
 *
 * The three load together rather than one at a time. A page that showed a verdict and then filled
 * in two sub-readings a second later would read as though the numbers disagreed.
 */
export async function fetchDetectors(
  base = 'model',
  onProgress: (fraction: number, note: string) => void = () => {},
): Promise<Detector[]> {
  onProgress(0, 'reading the list');
  const response = await fetch(`${base}/manifest.json`);
  if (!response.ok) {
    throw new ModelError(`${base}/manifest.json did not load: ${response.status}`);
  }
  const manifest = (await response.json()) as Manifest;

  if (manifest.format_version !== FORMAT_VERSION) {
    throw new ModelError(
      `manifest.json is format ${manifest.format_version}, and this build reads ` +
        `${FORMAT_VERSION}. Run \`uv run web/tools/export_model.py\` again`,
    );
  }
  const rows = manifest.detectors;
  if (!rows || rows.length === 0) {
    throw new ModelError('manifest.json names no detector');
  }
  if (!rows.some((row) => row.role === 'verdict')) {
    throw new ModelError('manifest.json names no verdict detector');
  }

  let done = 0;
  const built: Detector[] = [];
  for (const row of rows) {
    onProgress(done / rows.length, `reading ${row.display_name}`);
    built.push(await fetchOne(base, row.slug));
    done += 1;
  }

  onProgress(1, 'ready');
  return built;
}

/** Return the headline detector of a set, or `null` when none carries the role. */
export function verdictOf(built: Detector[]): Detector | null {
  return built.find((one) => one.meta.role === 'verdict') ?? null;
}

/** Return the sub-readings of a set, in manifest order. */
export function modelsOf(built: Detector[]): Detector[] {
  return built.filter((one) => one.meta.role === 'model');
}
